import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/Einwohnermeldeamt/SEOHead';
import Breadcrumb from '../../components/Einwohnermeldeamt/Breadcrumb';
import LocationCard from '../../components/Einwohnermeldeamt/LocationCard';
import LocationCTA from '../../components/Einwohnermeldeamt/LocationCTA';
import {
  getLocationsByBundesland,
  getBundeslandName,
  generateBreadcrumbs,
  formatNumber
} from '../../utils/einwohnermeldeamtHelpers';
import { Einwohnermeldeamt, BreadcrumbItem } from '../../types/einwohnermeldeamt';

const BundeslandPage: React.FC = () => {
  const { bundesland } = useParams<{ bundesland: string }>();
  const [locations, setLocations] = useState<Einwohnermeldeamt[]>([]);
  const [bundeslandName, setBundeslandName] = useState<string>('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'population' | 'plz'>('name');

  // Filter and sort locations - must be called before any early returns
  const filteredAndSortedLocations = useMemo(() => {
    let filtered = locations;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = locations.filter(
        loc =>
          loc.gemeindeName.toLowerCase().includes(query) ||
          (loc.postleitzahl && loc.postleitzahl.includes(query)) ||
          (loc.kreis && loc.kreis.toLowerCase().includes(query)) ||
          (loc.ort && loc.ort.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'population':
          return (b.einwohner || 0) - (a.einwohner || 0);
        case 'plz':
          return (a.postleitzahl || '').localeCompare(b.postleitzahl || '');
        case 'name':
        default:
          return a.gemeindeName.localeCompare(b.gemeindeName);
      }
    });

    return sorted;
  }, [locations, searchQuery, sortBy]);

  useEffect(() => {
    const loadData = async () => {
      if (!bundesland) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const [locationsData, nameData, breadcrumbsData] = await Promise.all([
          getLocationsByBundesland(bundesland),
          getBundeslandName(bundesland),
          generateBreadcrumbs(bundesland)
        ]);

        if (!nameData || locationsData.length === 0) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setLocations(locationsData);
        setBundeslandName(nameData);
        setBreadcrumbs(breadcrumbsData);
      } catch (error) {
        console.error('Error loading bundesland data:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [bundesland]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-prussian-blue mb-4"></div>
            <p className="text-gray-600">Lade Daten...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/einwohnermeldeamt" replace />;
  }

  const totalPopulation = locations.reduce((sum, loc) => sum + (loc.einwohner || 0), 0);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Einwohnermeldeämter in ${bundeslandName}`,
    description: `Vollständiges Verzeichnis aller Einwohnermeldeämter in ${bundeslandName}. Finden Sie Kontaktdaten und Informationen zu Ihrem lokalen Meldeamt.`,
    url: `https://www.adressfinder.org/einwohnermeldeamt/${bundesland}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: `https://www.adressfinder.org${crumb.path}`
      }))
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={`Einwohnermeldeämter in ${bundeslandName} | AdressFinder.org`}
        description={`📍 ${locations.length} Einwohnermeldeämter in ${bundeslandName} ✓ Kontaktdaten ✓ Adressen ✓ Öffnungszeiten ✓ Online-Adressauskunft • Jetzt finden!`}
        canonical={`https://www.adressfinder.org/einwohnermeldeamt/${bundesland}`}
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-8 md:py-12 px-4 bg-gradient-to-br from-blue-50 to-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <Breadcrumb items={breadcrumbs} />
            
            <h1 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ color: '#023047', lineHeight: '1.2' }}
            >
              Einwohnermeldeämter in {bundeslandName}
            </h1>
            
            <p className="text-lg text-gray-700 mb-6 max-w-3xl">
              Hier finden Sie alle Einwohnermeldeämter in {bundeslandName}. 
              Wählen Sie Ihre Gemeinde aus, um Kontaktdaten und weitere Informationen zu erhalten.
            </p>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold mb-1" style={{ color: '#023047' }}>
                  {formatNumber(locations.length)}
                </div>
                <div className="text-sm text-gray-600">Meldeämter</div>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl font-bold mb-1" style={{ color: '#023047' }}>
                  {formatNumber(totalPopulation)}
                </div>
                <div className="text-sm text-gray-600">Einwohner gesamt</div>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm col-span-2 md:col-span-1">
                <div className="text-2xl font-bold mb-1" style={{ color: '#023047' }}>
                  24/7
                </div>
                <div className="text-sm text-gray-600">Online verfügbar</div>
              </div>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="py-8 px-4 bg-white border-b">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              {/* Search */}
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  Suche nach Gemeinde, PLZ oder Kreis
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="z.B. München, 80331 oder Landkreis..."
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              {/* Sort */}
              <div className="w-full md:w-64">
                <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-2">
                  Sortieren nach
                </label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'population' | 'plz')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="name">Name (A-Z)</option>
                  <option value="population">Einwohnerzahl</option>
                  <option value="plz">Postleitzahl</option>
                </select>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-4 text-sm text-gray-600">
              {filteredAndSortedLocations.length === locations.length ? (
                <span>{formatNumber(locations.length)} Meldeämter</span>
              ) : (
                <span>
                  {formatNumber(filteredAndSortedLocations.length)} von {formatNumber(locations.length)} Meldeämtern
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Locations Grid */}
        <section className="py-8 px-4 bg-gray-50">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            {filteredAndSortedLocations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedLocations.map(location => (
                  <LocationCard key={location.amtlGemeindeschluessel || location.slug} location={location} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <svg
                  className="w-16 h-16 mx-auto mb-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xl text-gray-600 mb-2">Keine Ergebnisse gefunden</p>
                <p className="text-gray-500">
                  Versuchen Sie es mit einem anderen Suchbegriff
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto" style={{ maxWidth: '1000px' }}>
            <LocationCTA locationName={bundeslandName} />
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BundeslandPage;

